import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from './modules/i18n';
import { SplashScreenService } from './_metronic/partials/layout/splash-screen/splash-screen.service';
// language list
import { locale as enLang } from './modules/i18n/vocabs/en';
import { locale as trLang } from './modules/i18n/vocabs/tr';
import { ThemeModeService } from './_metronic/partials/layout/theme-mode-switcher/theme-mode.service';
import Swal from 'sweetalert2';
import { IosZoomLockService } from './core/services/ios-zoom-lock.service';

@Component({
  // tslint:disable-next-line:component-selector
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body[root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private autocompleteObserver?: MutationObserver;

  constructor(
    private translationService: TranslationService,
    private translate: TranslateService,
    private modeService: ThemeModeService,
    private splashScreenService: SplashScreenService,
    private iosZoomLockService: IosZoomLockService
  ) {
    // register translations
    this.translationService.loadTranslations(enLang, trLang);
  }

  ngOnInit() {
    this.modeService.init();
    this.translationService.updateSplashLoadingText();
    this.hideSplashScreen();
    this.handleSwUpdate();
    this.iosZoomLockService.init();
    this.disableBrowserAutocomplete();
  }

  ngOnDestroy(): void {
    this.autocompleteObserver?.disconnect();
  }

  private disableBrowserAutocomplete(): void {
    const apply = (root: ParentNode) => {
      root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea').forEach((field) => {
        field.setAttribute('autocomplete', 'off');
      });
    };

    apply(document);
    this.autocompleteObserver = new MutationObserver((records) => {
      for (const record of records) {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches('input, textarea')) node.setAttribute('autocomplete', 'off');
          apply(node);
        });
      }
    });
    this.autocompleteObserver.observe(document.body, { childList: true, subtree: true });
  }

  private handleSwUpdate(): void {
    if (!this.swUpdate) {
      return;
    }
    this.swUpdate.versionUpdates.subscribe((evt) => {
      if (evt.type === 'VERSION_READY') {
        Swal.fire({
          title: this.translate.instant('PWA.UPDATE_TITLE'),
          text: this.translate.instant('PWA.UPDATE_TEXT'),
          icon: 'info',
          showCancelButton: true,
          confirmButtonText: this.translate.instant('PWA.UPDATE_CONFIRM'),
          cancelButtonText: this.translate.instant('PWA.UPDATE_CANCEL'),
        }).then((result) => {
          if (result.isConfirmed) {
            document.location.reload();
          }
        });
      }
    });
  }

  private hideSplashScreen(): void {
    const splashEl = document.getElementById('splash-screen');
    if (!splashEl) {
      return;
    }
    this.splashScreenService.init(new ElementRef(splashEl));
    setTimeout(() => this.splashScreenService.hide(), 50);
  }
}
